import { motion } from 'framer-motion';
import { FileText } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

export default function TermsOfServicePage() {
  return (
    <div data-testid="terms-of-service-page" className="bg-[#0E2A47] min-h-screen">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-overlay" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="mb-6">
              <div className="w-16 h-16 rounded-sm bg-[#0FA4A9]/10 flex items-center justify-center mx-auto">
                <FileText className="h-8 w-8 text-[#0FA4A9]" />
              </div>
            </motion.div>
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4"
            >
              Terms of Service
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="font-body text-white/60"
            >
              Effective Date: February 15, 2026 | Last Updated: February 15, 2026
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-[#0A1F35]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="prose prose-invert max-w-none"
          >
            <div className="glass-card rounded-sm p-8 sm:p-12 space-y-10">
              {/* Intro */}
              <p className="font-body text-white/80 text-lg leading-relaxed">
                Welcome to Discovify AI. These Terms of Service ("Terms") govern your use of our website and services. By accessing or using our website, booking a call, or submitting any forms, you agree to be bound by these Terms.
              </p>

              {/* Section 1 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  1. ACCEPTANCE OF TERMS
                </h2>
                <p className="font-body text-white/70">
                  By using our website or services, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree, please do not use our services.
                </p>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  2. DESCRIPTION OF SERVICES
                </h2>
                <p className="font-body text-white/70 mb-4">
                  Discovify AI provides:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-4">
                  <li>AI and automation consulting services</li>
                  <li>Workflow analysis and optimization</li>
                  <li>Business process automation solutions</li>
                  <li>AI implementation services and workshops</li>
                  <li>Discovery calls and consultations</li>
                  <li>Information about potential future services (such as AI recruiting)</li>
                </ul>
                <p className="font-body text-white/70">
                  Some services are in development or exploration phases and may not be immediately available.
                </p>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  3. USER REPRESENTATIONS
                </h2>
                <p className="font-body text-white/70 mb-4">
                  By using our services, you represent and warrant that:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>You are at least 18 years of age</li>
                  <li>You have the legal capacity to enter into these Terms</li>
                  <li>All information you provide is accurate and truthful</li>
                  <li>You will not use our services for any unlawful purpose</li>
                  <li>You have authority to bind your company to communications with us (if representing a business)</li>
                </ul>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  4. BOOKING AND CONSULTATIONS
                </h2>
                
                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Calendar Bookings:
                </h3>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-6">
                  <li>When you book a discovery call, you consent to receive appointment confirmations and reminders</li>
                  <li>You agree to provide accurate contact information</li>
                  <li>You may cancel or reschedule appointments according to the procedures specified in booking confirmations</li>
                  <li>We reserve the right to cancel or reschedule appointments if necessary</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Discovery Calls:
                </h3>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Discovery calls are provided for informational purposes</li>
                  <li>No guarantees or warranties are made during discovery calls</li>
                  <li>Information shared during calls may be used to understand your needs and provide relevant services</li>
                </ul>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  5. COMMUNICATIONS AND CONSENT
                </h2>
                <p className="font-body text-white/70 mb-4">
                  By submitting forms or booking calls, you consent to:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Receive automated emails related to your inquiry or booking</li>
                  <li>Receive follow-up communications about services discussed</li>
                  <li>Receive marketing communications about our services (you may opt-out at any time)</li>
                  <li>Be contacted via email, phone, or other contact methods you provide</li>
                </ul>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  6. USE OF WEBSITE
                </h2>
                <p className="font-body text-white/70 mb-4">
                  You agree to:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Use the website only for lawful purposes</li>
                  <li>Not attempt to gain unauthorized access to our systems</li>
                  <li>Not transmit viruses, malware, or harmful code</li>
                  <li>Not scrape, copy, or misuse our website content</li>
                  <li>Not impersonate others or provide false information</li>
                </ul>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  7. INTELLECTUAL PROPERTY
                </h2>
                <p className="font-body text-white/70 mb-4">
                  All content on this website, including:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-4">
                  <li>Text, graphics, logos, designs</li>
                  <li>Website code and functionality</li>
                  <li>Service descriptions and methodologies</li>
                  <li>Proprietary frameworks and processes</li>
                </ul>
                <p className="font-body text-white/70 mb-4">
                  ...is owned by Discovify AI or licensed to us and is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="font-body text-white/70 mb-4">
                  You may not:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Copy, modify, or distribute our content without permission</li>
                  <li>Use our trademarks or branding without authorization</li>
                  <li>Reverse engineer any aspect of our website or services</li>
                </ul>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  8. NO GUARANTEES
                </h2>
                <p className="font-body text-white/70 mb-4">
                  While we strive to provide valuable services:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>We make no guarantees about specific business results</li>
                  <li>Discovery calls are exploratory and do not constitute professional advice</li>
                  <li>Services in development (like AI recruiting) may not launch or may differ from descriptions</li>
                  <li>We do not guarantee timelines for delivery of services</li>
                </ul>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  9. LIMITATION OF LIABILITY
                </h2>
                <p className="font-body text-white/70 mb-4">
                  To the fullest extent permitted by law:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Discovify AI is not liable for any indirect, incidental, special, or consequential damages</li>
                  <li>Our total liability shall not exceed the amount you paid us for services (if any)</li>
                  <li>We are not liable for service interruptions, data loss, or technical issues</li>
                  <li>We are not responsible for third-party services or links</li>
                </ul>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  10. INDEMNIFICATION
                </h2>
                <p className="font-body text-white/70 mb-4">
                  You agree to indemnify and hold harmless Discovify AI from any claims, damages, or expenses arising from:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Your violation of these Terms</li>
                  <li>Your use of our services</li>
                  <li>Your violation of any third-party rights</li>
                  <li>Any false or misleading information you provide</li>
                </ul>
              </div>

              {/* Section 11 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  11. MODIFICATIONS TO SERVICES AND TERMS
                </h2>
                <p className="font-body text-white/70 mb-4">
                  We reserve the right to:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-4">
                  <li>Modify or discontinue services at any time</li>
                  <li>Update these Terms with or without notice</li>
                  <li>Change pricing or service offerings</li>
                  <li>Terminate access to services</li>
                </ul>
                <p className="font-body text-white/70">
                  Continued use of our services after changes constitutes acceptance of modified Terms.
                </p>
              </div>

              {/* Section 12 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  12. THIRD-PARTY SERVICES
                </h2>
                <p className="font-body text-white/70 mb-4">
                  Our services may integrate with or reference third-party tools, platforms, or services:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>We are not responsible for third-party services or their terms</li>
                  <li>Your use of third-party services is governed by their terms</li>
                  <li>We may receive compensation for recommending certain tools</li>
                </ul>
              </div>

              {/* Section 13 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  13. PAYMENT TERMS
                </h2>
                <p className="font-body text-white/70 mb-4">
                  For paid services:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70">
                  <li>Payment terms will be specified in service agreements or invoices</li>
                  <li>All fees are non-refundable unless otherwise stated</li>
                  <li>You are responsible for all applicable taxes</li>
                  <li>Late payments may result in service suspension</li>
                </ul>
              </div>

              {/* Section 14 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  14. TERMINATION
                </h2>
                <p className="font-body text-white/70 mb-4">
                  We may terminate or suspend your access to our services:
                </p>
                <ul className="list-disc list-inside space-y-2 font-body text-white/70 mb-4">
                  <li>For violation of these Terms</li>
                  <li>For fraudulent or illegal activity</li>
                  <li>At our discretion with or without notice</li>
                </ul>
                <p className="font-body text-white/70">
                  You may terminate your relationship with us by ceasing to use our services and requesting deletion of your information.
                </p>
              </div>

              {/* Section 15 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  15. DISPUTE RESOLUTION
                </h2>
                
                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Governing Law:
                </h3>
                <p className="font-body text-white/70 mb-6">
                  These Terms are governed by the laws of the State of New York, United States.
                </p>

                <h3 className="font-heading text-lg font-semibold text-[#0FA4A9] mb-3">
                  Informal Resolution:
                </h3>
                <p className="font-body text-white/70">
                  Before filing any legal claim, you agree to attempt to resolve disputes by contacting us at <a href="mailto:info@discovify.ai" className="text-[#0FA4A9] hover:underline">info@discovify.ai</a>.
                </p>
              </div>

              {/* Section 16 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  16. SEVERABILITY
                </h2>
                <p className="font-body text-white/70">
                  If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
                </p>
              </div>

              {/* Section 17 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  17. ENTIRE AGREEMENT
                </h2>
                <p className="font-body text-white/70">
                  These Terms, together with our Privacy Policy, constitute the entire agreement between you and Discovify AI regarding use of our services.
                </p>
              </div>

              {/* Section 18 */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-white mb-4">
                  18. CONTACT INFORMATION
                </h2>
                <p className="font-body text-white/70 mb-4">
                  For questions about these Terms or our services:
                </p>
                <div className="bg-[#1E4B75]/50 rounded-sm p-6 mb-6">
                  <p className="font-heading text-lg font-bold text-white mb-2">Discovify AI</p>
                  <p className="font-body text-white/70">
                    Email: <a href="mailto:info@discovify.ai" className="text-[#0FA4A9] hover:underline">info@discovify.ai</a>
                  </p>
                  <p className="font-body text-white/70">
                    Website: <a href="https://www.discovify.ai" className="text-[#0FA4A9] hover:underline">www.discovify.ai</a>
                  </p>
                </div>
                <p className="font-body text-white/70 italic">
                  By using our website or services, you acknowledge that you have read and understood these Terms of Service.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
